/**
 * BehaviourSlot
 *
 * Renders an inline slot container for block behaviours.
 * When a block has auto-triggered behaviours of type `agent_experience`
 * with experience `insight_preview`, this component renders the nested
 * blocks from the behaviour's data inline below the parent block.
 *
 * Supports a fullscreen toggle to expand the slot to full chat width.
 */

import React from 'react';
import { AnimatePresence } from 'motion/react';
import type {
  BlockBehaviour,
  BehaviourAgentExperienceAction,
  UIActionEvent,
} from '@/types/ui-blocks';
import { BlocksPreviewSlot } from './slots/blocks-preview-slot';
import { ContentRefreshPreviewSlot } from './slots/content-refresh-preview-slot';
import { ContentOutlinePreviewSlot } from './slots/content-outline-preview-slot';
import { ContentPreviewSlot } from './slots/content-preview-slot';
import { KeywordReviewPreviewSlot } from './slots/keyword-review-preview-slot';
import { ExperimentDetailSlot } from './slots/experiment-detail-slot';

export interface BehaviourSlotProps {
  /** List of behaviours from the parent block */
  behaviours: BlockBehaviour[];
  /** Callback fired when user interacts with UI components inside the slot */
  onUIAction: (event: UIActionEvent) => void;
  /** When true, interactive elements are disabled */
  readOnly?: boolean;
  /** Chat history ID for the message containing this slot */
  chatHistoryId?: string;
  /** Behaviour action state map keyed by action ID */
  behaviourActionState?: Record<string, unknown>;
}

/**
 * Type guard for agent_experience actions
 */
const isAgentExperienceAction = (
  action: BlockBehaviour['action']
): action is BehaviourAgentExperienceAction => {
  return action.type === 'agent_experience';
};

/**
 * Main BehaviourSlot component.
 * Filters for auto-triggered behaviours and renders the appropriate slot
 * based on the action type and experience.
 */
export const BehaviourSlot: React.FC<BehaviourSlotProps> = ({
  behaviours,
  onUIAction,
  readOnly,
  chatHistoryId,
  behaviourActionState,
}) => {
  // Filter for auto-triggered behaviours only
  const autoBehaviours = behaviours.filter((b) => b.trigger === 'auto');

  if (autoBehaviours.length === 0) {
    return null;
  }

  return (
    <AnimatePresence initial={false}>
      {autoBehaviours.map((behaviour) => {
        const { action } = behaviour;

        if (isAgentExperienceAction(action)) {
          let slotContent: React.ReactNode = null;

          switch (action.experience) {
            case 'insight_preview':
            case 'report_preview':
              slotContent = (
                <BlocksPreviewSlot
                  action={action}
                  onUIAction={onUIAction}
                  readOnly={readOnly}
                  chatHistoryId={chatHistoryId}
                  behaviourActionState={behaviourActionState}
                />
              );
              break;
            case 'blog_refresh_queue':
              slotContent = (
                <ContentRefreshPreviewSlot
                  action={action}
                  chatHistoryId={chatHistoryId}
                  behaviourActionState={behaviourActionState}
                />
              );
              break;
            case 'content_outline_queue':
              slotContent = (
                <ContentOutlinePreviewSlot
                  action={action}
                  chatHistoryId={chatHistoryId}
                  behaviourActionState={behaviourActionState}
                />
              );
              break;
            case 'content_editor':
              slotContent = (
                <ContentPreviewSlot
                  action={action}
                  chatHistoryId={chatHistoryId}
                  behaviourActionState={behaviourActionState}
                />
              );
              break;
            case 'keyword_review':
              slotContent = (
                <KeywordReviewPreviewSlot
                  action={action}
                  readOnly={readOnly}
                  chatHistoryId={chatHistoryId}
                  behaviourActionState={behaviourActionState}
                />
              );
              break;
            case 'experiments':
              slotContent = (
                <ExperimentDetailSlot
                  action={action}
                  readOnly={readOnly}
                />
              );
              break;
          }

          if (slotContent) {
            return (
              <div key={behaviour.id} data-slot-id={behaviour.id}>
                {slotContent}
              </div>
            );
          }
        }

        return null;
      })}
    </AnimatePresence>
  );
};

export default BehaviourSlot;
